import { setTags } from '@sentry/react'
import { makeAutoObservable, runInAction } from 'mobx'
import { api } from 'store/api'
import { opt } from 'store/opt'
import { UserData } from 'type/Me'
import { Metrics } from 'type/metrics'
import { Options } from 'type/Options'
import { fixTerm, getTermUntil, loadTerm, saveTerm, Term } from 'type/term'
import { logUser } from 'util/adminDebug'
import { getProdCookie, setProdCookie } from 'util/cookie'
import { omit } from 'util/object'
import { load, save } from 'util/storage'

const USER = 'user'

function generateRandomHash(): string {
  let hash = ''
  for (let n = 0; n < 32; ++n) {
    hash += Math.trunc(Math.random() * 36).toString(36)[0]
  }
  return hash
}

export function loadUser(company: string): string | undefined {
  const cookie = getProdCookie(USER + '.' + company)
  if (cookie) return cookie

  const user = load(USER + '.' + company)

  setProdCookie(USER + '.' + company, user || undefined)
  return user || undefined
}

export function saveUser(company: string, user: string | undefined) {
  save(USER + '.' + company, user || undefined)
  setProdCookie(USER + '.' + company, user || undefined)
  logUser('saveUser', { user: user ?? null })
}

export function initUser(company: string): string {
  const user = generateRandomHash()
  saveUser(company, user)
  return user
}

class MeStore {
  company: string = 'unknown'
  external_user?: string
  external = false
  manager_id?: string
  private _term: Term | undefined
  private _policy = false
  user!: string
  metrics: Metrics = {}
  private _user_id = ''
  private _email = ''
  private _creator?: () => Promise<void>
  private _creating?: Promise<void>
  private _picture: string | undefined

  constructor() {
    makeAutoObservable(this)
  }

  set creator(value: () => Promise<void>) {
    this._creator = async () => {
      try {
        await value()
      } catch (e) {
        console.error('creator failed', e)
        this._creating = undefined
      }
    }
  }

  get ensure(): Promise<void> {
    if (this.user_id) return Promise.resolve()
    if (this._creating) return this._creating
    if (!this._creator) return Promise.reject(new Error('no user creator'))

    const creating = this._creator()
    runInAction(() => this._creating = creating)
    return creating
  }

  setup(options: Options) {
    const { company, user, manager } = options
    this.company = company || 'unknown'
    this.external_user = user || undefined
    this.external = !!user
    this.manager_id = manager || undefined
    logUser('setup', { external_user: this.external_user ?? null, external: this.external })
    this.reset()
  }

  init(user: UserData, metrics?: Metrics) {
    const { user_id, email, picture, personal_data_consent } = user
    this.user_id = user_id
    this.email = email || ''
    this._picture = picture || undefined
    this._policy = !!personal_data_consent
    this.metrics = metrics ?? {}
    logUser('init (from API)', {
      user_id,
      email: email ?? null,
      policy: !!personal_data_consent,
      local_user: this.user,
    })
  }

  get user_id(): string {
    return this._user_id
  }

  set user_id(user_id: string) {
    this._user_id = user_id
    setTags({ user_id })
  }

  get email(): string {
    return this._email
  }

  set email(email: string) {
    this._email = email
    setTags({ email: email || undefined })
  }

  get term(): Term | undefined {
    return this._term
  }

  set term(term: Term | undefined) {
    term = fixTerm(term)
    this._term = term
    saveTerm(this.user_id, term)
  }

  get policy(): boolean {
    return this._policy || opt.basic
  }

  set policy(value: boolean) {
    this._policy = value
  }

  get picture(): string | undefined {
    return this._picture
  }

  set picture(value: string | undefined) {
    if (this.picture === value) {
    } else if (this.user_id) {
      this._picture = value
      void api.updateUser({ pic: this.picture })
    } else {
      this._picture = undefined
    }
  }

  updateProfile(metrics: Metrics) {
    const replace = this.metrics.gender && this.metrics.gender !== metrics.gender
    const remove = replace ? Object.fromEntries(Object.keys(this.metrics).map(key => [key, null])) : {}
    const update = { ...remove, ...omit(metrics) }
    this.metrics = { ...this.metrics, ...update }
    void api.saveProfile(update)
  }

  deleteUser(kill?: boolean) {
    logUser('deleteUser called', { kill: !!kill })
    this.resetStorage()
    // при kill виджет уничтожается целиком: новый пользователь не нужен,
    // иначе initUser сразу же запишет обратно user.${company_id}
    if (!kill) this.reset()
  }

  private reset() {
    this.initUser()
    this.metrics = {}
    // Загружаем term только если уже есть user_id (повторный setup в той же сессии).
    // При первой загрузке страницы user_id всегда '', loadTerm('') = undefined — не трогаем пользователя.
    this._term = this._user_id ? loadTerm(this.user_id) : undefined
    this._policy = false
    this.ensureTerm()
    logUser('reset done', { user: this.user, had_user_id: !!this._user_id, term: this._term ?? null })
    this._user_id = ''
    this._email = ''
    this._creating = undefined
    const { external_user, user } = this
    setTags({ external_user, user })
  }

  private ensureTerm() {
    if (this.external) return
    if (this.expired) {
      logUser('ensureTerm: term expired, clearing user', { term: this._term })
      this._term = undefined
      this.resetStorage()
      this.initUser()
    } else if (this._term) {
      const { type } = this._term
      const created = Date.now()
      this.term = { type, created }
    }
  }

  private initUser() {
    const fromStorage = loadUser(this.company)
    const source = this.external_user ? 'external_user' : fromStorage ? 'localStorage' : 'new'
    this.user = this.external_user || fromStorage || initUser(this.company)
    logUser('initUser', { source, user: this.user, had_from_storage: !!fromStorage })
  }

  private resetStorage() {
    logUser('resetStorage', { cleared_user_id: this._user_id || null })
    this.external_user = undefined
    this.external = false
    saveTerm(this.user_id, undefined)
    saveUser(this.company, undefined)
  }

  private get expired(): boolean {
    if (!this._term) return false
    const end = getTermUntil(this._term)
    if (!end) return false
    const interval = end - Date.now()
    return interval < 0
  }
}

export const me = new MeStore()
